import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import { ModalV2 } from "../../secondary/Modal/ModalV2";
import { InputField } from "../../primary/InputField";
import { SelectField } from "../../primary/SelectField";
import { TextAreaField } from "../../primary/TextAreaField";
import { FormActions } from "../../secondary/FormActions/FormActions";
import { useCreateAnnouncementMutation, useUpdateAnnouncementMutation, useAnnouncementDetail } from "../../../features/announcements";
import { userNotificationAsync } from "../../../app/userSlice";
import { SALES_OFFICE_OPTIONS } from "../../../constants";
import constants from "../../../constants/constants.json";
import { StatusBanner } from "../../secondary/StatusBanner";
import shared from "../../../styles/common.module.scss";
import style from "./AnnouncementFormModal.module.scss";

interface AnnouncementFormValues {
  title: string;
  salesOffice: string;
  description: string;
}

interface AnnouncementFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (message: string) => void;
  announcementId?: number;
}

export const AnnouncementFormModal: React.FC<AnnouncementFormModalProps> = ({ open, onClose, onSuccess, announcementId }) => {
  const dispatch = useDispatch();
  const isEdit = announcementId !== undefined;

  const createMutation = useCreateAnnouncementMutation();
  const updateMutation = useUpdateAnnouncementMutation();
  const activeMutation = isEdit ? updateMutation : createMutation;

  const { data: announcementDetail, isLoading: isDetailLoading } = useAnnouncementDetail(announcementId);

  const { control, handleSubmit, reset, errors } = useForm<AnnouncementFormValues>({
    mode: "all",
    defaultValues: { title: "", salesOffice: "", description: "" },
  });

  useEffect(() => {
    if (isEdit && announcementDetail) {
      reset({
        title: announcementDetail.title ?? "",
        salesOffice: announcementDetail.salesOffice ?? "",
        description: announcementDetail.description ?? "",
      });
    }
  }, [announcementDetail, isEdit, reset]);

  useEffect(() => {
    if (!open) {
      reset({ title: "", salesOffice: "", description: "" });
      createMutation.reset();
      updateMutation.reset();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Submit ──────────────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const onSubmit = async (values: AnnouncementFormValues) => {
    setIsSubmitting(true);
    try {
      if (isEdit && announcementId !== undefined) {
        await updateMutation.mutateAsync({
          id: announcementId,
          title: values.title,
          salesOffice: values.salesOffice,
          description: values.description,
        });
      } else {
        await createMutation.mutateAsync({
          title: values.title,
          salesOffice: values.salesOffice,
          description: values.description,
        });
      }
      dispatch(userNotificationAsync());
      onSuccess?.(isEdit ? constants.announcement.announcementUpdatedMsg : constants.announcement.announcementCreatedMsg);
      onClose();
    } catch {
      // activeMutation.isError will be true; error shown inline below.
    } finally {
      setIsSubmitting(false);
    }
  };

  const heading = isEdit ? constants.announcement.editTitle : constants.announcement.createTitle;

  return (
    <ModalV2 isOpen={open} onClose={onClose} title={heading}>
      {isEdit && isDetailLoading ? (
        <div className={style.loadingRow}>
          <span className={shared.neutral_spinner} aria-hidden="true" />
          Loading…
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className={style.formBody}>
            <Controller
              control={control}
              name="title"
              rules={{
                required: constants.announcement.nameRequired,
                maxLength: {
                  value: constants.announcement.nameLimit,
                  message: constants.announcement.nameLimitRequired,
                },
              }}
              render={({ onChange, onBlur, value, ref }) => (
                <InputField
                  id="announcementTitle"
                  name="title"
                  label={constants.announcement.announcementName}
                  required
                  charLimit={constants.announcement.nameLimit}
                  value={value ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={errors.title?.message}
                  dataTestId="announcementTitle"
                />
              )}
            />

            <Controller
              control={control}
              name="salesOffice"
              rules={{ required: constants.announcement.salesOfficeRequired }}
              render={({ onChange, onBlur, value, ref }) => (
                <SelectField
                  id="salesOffice"
                  name="salesOffice"
                  label={constants.announcement.salesOffice}
                  required
                  options={SALES_OFFICE_OPTIONS}
                  placeholder={constants.announcement.selectSalesOffice}
                  value={value ?? ""}
                  onChange={(e: { target: { value: string | number } }) => onChange(String(e.target.value))}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={errors.salesOffice?.message}
                  dataTestId="salesOffice"
                />
              )}
            />

            <Controller
              control={control}
              name="description"
              rules={{
                required: constants.announcement.descriptionRequired,
                maxLength: {
                  value: constants.announcement.descriptionLimit,
                  message: constants.announcement.descriptionLimitRequired,
                },
              }}
              render={({ onChange, onBlur, value, ref }) => (
                <TextAreaField
                  id="announcementDescription"
                  name="description"
                  label={constants.announcement.announcementDescription}
                  required
                  rows={5}
                  charLimit={constants.announcement.descriptionLimit}
                  value={value ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={errors.description?.message}
                  dataTestId="announcementDescription"
                />
              )}
            />

            {activeMutation.isError && (
              <StatusBanner
                status="error"
                message={isEdit ? constants.announcement.announcementNotUpdatedMsg : constants.announcement.announcementNotCreatedMsg}
              />
            )}
          </div>

          <FormActions
            primaryLabel={isEdit ? constants.announcement.update : constants.announcement.save}
            primaryType="submit"
            primaryDisabled={isSubmitting}
            primaryLoading={isSubmitting}
            onPrimaryClick={undefined}
            secondaryLabel={constants.announcement.cancel}
            onSecondaryClick={onClose}
          />
        </form>
      )}
    </ModalV2>
  );
};
